import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { isMatchIgnoreCase } from '@shared/utils/helpers';
import { IConfiguration } from '@modules/admin/AdminTools/ConfigurationTable/types';
import { SERVICES } from '@shared/constants/apiConfig';
import { IGeneralProducts } from './EditRetailForm';

const FLORIDA_STATE = 'FL';
const PRODUCT_TYPE_VSC = 'VSC';
const PRODUCTS = 'PRODUCTS';
const FL_VSC_MARKUP_MAX = 'FL_VSC_MARKUP_MAX';
const DEFAULT_FL_VSC_MARKUP_MAX = '687';

export const useRetailValidation = (
    productsList?: IGeneralProducts[] | null,
    markupApplied?: { [key: string]: number } | null,
) => {
    const { data: configurationTables, isValidating: isValidatingConfiguration } = useSWR<IConfiguration[]>(
        `${SERVICES.CONFIGURATION}/configurations/all`
    );

    const isConfigurationLoading = useMemo(
        () => !configurationTables || isValidatingConfiguration,
        [configurationTables, isValidatingConfiguration]
    );

    const flVscMarkupMax = useMemo(
        () =>
            configurationTables?.find(
                (c) => c.configurationGroup === PRODUCTS && c.tokenName === FL_VSC_MARKUP_MAX
            )?.value ?? DEFAULT_FL_VSC_MARKUP_MAX,
        [configurationTables]
    );

    const getFlVscMarkupAlertText = (exceededMaxAllowedRetail: number | null = null) => {
        switch (true) {
            case exceededMaxAllowedRetail !== null:
                return `The retail value you have entered exceeds the maximum allowed retail of $${exceededMaxAllowedRetail}. Do you want to proceed?`;         
            default:
                return `FL VSC Markup Amount must be $${flVscMarkupMax}. Please adjust Add-On Selections.`;
        }
    };

    const isFlVscMarkupInvalid = useCallback(
        (productType: string, markup: number, contactState?: string) => {
            return (
                isMatchIgnoreCase(contactState || '', FLORIDA_STATE) &&
                isMatchIgnoreCase(productType || '', PRODUCT_TYPE_VSC) &&
                markup !== parseInt(flVscMarkupMax)
            );
        },
        [flVscMarkupMax]
    );

    const validateProducts = useCallback((contactState?: string): boolean => {
        if (!productsList?.length) {
            return false;
        }

        for (const product of productsList) {
            const productName = product?.productTypeName || '';
            const markupAppliedValue = markupApplied?.[product?.productTypeId || ''] ?? product?.markup ?? 0;

            if (!productName) {
                return false;
            }

            if (isFlVscMarkupInvalid(productName || '', markupAppliedValue, contactState)) {
                return false;
            }
        }

        return true;
    }, [isFlVscMarkupInvalid, markupApplied, productsList]);

    return {
        getFlVscMarkupAlertText,
        isConfigurationLoading,
        validateProducts,
        isFlVscMarkupInvalid,
    };
};
